// Player list — free tier sees names/positions/hot-cold; tapping opens the
// Elite detail sheet (locked → onUpgrade).

import React from 'react';
import { ChevronRight, Lock } from 'lucide-react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Card, JerseyChip, shortName } from './ui';
import type { SeasonData } from './useSeasonStats';

export const PlayerGrid = ({ data, elite, onOpenPlayer, onUpgrade }: {
  data: SeasonData;
  elite: boolean;
  onOpenPlayer: (playerId: string) => void;
  onUpgrade?: () => void;
}) => {
  const rows = Object.values(data.stats)
    .filter((s: any) => s.batting.pa > 0 || s.batting.ab > 0 || s.gp > 0)
    .sort((a: any, b: any) => (b.batting.ops ?? -1) - (a.batting.ops ?? -1));

  if (rows.length === 0) return null;

  const toneFor = (pid: string): 'default' | 'hot' | 'cold' => {
    const group = data.signalsByPlayer.find(g => g.playerId === pid);
    if (!group) return 'default';
    return group.signals.some(s => s.direction === 'hot') ? 'hot' : 'cold';
  };

  return (
    <Card>
      {rows.map((s: any, idx: number) => {
        const b = s.batting;
        const tone = toneFor(s.player.id);
        const topSignal = data.signalsByPlayer.find(g => g.playerId === s.player.id)?.signals[0];
        return (
          <button
            key={s.player.id}
            onClick={() => elite ? onOpenPlayer(s.player.id) : onUpgrade?.()}
            className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:opacity-90"
            style={{ borderTop: idx > 0 ? `1px solid ${GS.line}` : undefined }}
          >
            <div className="relative">
              <JerseyChip number={s.player.number} tone={tone} />
              {tone !== 'default' && (
                <span className="absolute -bottom-1 -right-1 text-[11px]" aria-hidden="true">{tone === 'hot' ? '🔥' : '❄️'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-extrabold truncate" style={{ color: GS.ink }}>{shortName(s.player.name)}</p>
              <p className="text-[10px] font-semibold truncate mt-0.5" style={{ color: GS.ink2 }}>
                {topSignal ? topSignal.label : (s.player.positions?.[0] || '')}
              </p>
            </div>
            {elite ? (
              <>
                <div className="flex gap-3 text-right shrink-0">
                  {[
                    { v: fmtStat(b.avg, 'avg3'), l: 'AVG' },
                    { v: fmtStat(b.obp, 'avg3'), l: 'OBP' },
                    { v: fmtStat(b.qabPct, 'pct'), l: 'QAB' },
                  ].map(m => (
                    <div key={m.l}>
                      <div className="text-xs font-black tabular-nums" style={{ color: m.v === '—' ? GS.ink3 : GS.ink }}>{m.v}</div>
                      <div className="text-[8px] font-extrabold uppercase" style={{ color: GS.ink2, letterSpacing: '0.05em' }}>{m.l}</div>
                    </div>
                  ))}
                </div>
                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: GS.ink3 }} />
              </>
            ) : (
              <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: GS.goldInk }} />
            )}
          </button>
        );
      })}
    </Card>
  );
};
